import "../../../../assets/js/libs/smoothy/smoothy-mode.js";

import Editor, { useMonaco } from "@monaco-editor/react";
import iframeManager from "iframe";
import $ from "jquery";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { BiExpandAlt } from "react-icons/bi";
import { useParams } from "react-router-dom";

import reddoError from "../../../../assets/Reddo-error.gif";
import { useTheme } from "../../../../context/ThemeContext.jsx";
import useAuth from "../../../../hooks/useAuth.js";
import studentServices from "../../../../services/studentServices.js";
import { getLanguage } from "../../../../utils/getLanguage.js";
import Button from "../../../Button.jsx";
import GreenoMessage from "./GreenoMessage.jsx";
import SmoothyHeader from "./SmoothyHeader.jsx";

const SmoothyCodeEditor = ({
  isTeacher = false,
  initialCode,
  smoothy,
  setSmoothy,
  setIsStepsOver,
  message,
  setMessage,
  setProjectID,
  resetSmoothyCode = () => {},
  hasCodeContent = () => {},
  reRunCode = false,
}) => {
  const { type = "playground", lessonID } = useParams();

  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);
  const [isHorizontal, setIsHorizontal] = useState(true);
  const [editorDecorations, setEditorDecorations] = useState([]);
  const [currentStep, setCurrentStep] = useState(null);
  const [myHintSteps, setMyHintSteps] = useState([]);
  const [currentHintIndex, setCurrentHintIndex] = useState(0);
  const [myFootprints, setMyFootprints] = useState([]);
  const [footprintsMessages, setFootprintsMessages] = useState([]);
  const [currentStepNumber, setCurrentStepNumber] = useState(0);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [snippet, setSnippet] = useState(null);
  const lang = getLanguage();
  const outputRef = useRef(null);
  const editorRef = useRef(null);
  const { theme } = useTheme();
  const monaco = useMonaco();
  const user = useAuth();

  const handleGetCodeProject = async () => {
    setLoading(true);
    try {
      const response = await studentServices.getStudentCodeProject(lessonID);
      setMyHintSteps(
        response?.steps?.map((step) => ({
          title: step?.title,
          hints: step?.hints || [],
        }))
      );
      setMyFootprints(response?.assertion?.footprints || []);
      let message = response?.assertion?.message;

      if (typeof message === "string") {
        try {
          message = JSON.parse(message);
        } catch (e) {
          console.error("Failed to parse assertion message", e);
          message = [];
        }
      }

      setFootprintsMessages(message || []);
      if (response?.snippet?.code) {
        setSnippet(response?.snippet?.code);
      }
      setSmoothy(response?.student_solution?.code?.smoothy || response?.snippet?.code);
      onRunCode(response?.student_solution?.code?.smoothy || response?.snippet?.code);
      setProjectID(response?.student_solution?.id);
    } catch (error) {
      console.error("Error fetching code project:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (type == "code") {
      handleGetCodeProject();
    }
  }, []);

  useEffect(() => {
    if (monaco) {
      import("../../../../assets/js/libs/smoothy/smoothy-mode.js")
        .then(({ registerSmoothyLanguage }) => {
          // prevent double registration
          if (!monaco.languages.getLanguages().some((lang) => lang.id === "smoothy")) {
            registerSmoothyLanguage(monaco);
          }
        })
        .catch((err) => {
          console.error("Failed to load smoothy-mode.js:", err);
        });
    }
  }, [monaco]);
  useEffect(() => {
    if (!monaco) return;

    monaco.editor.defineTheme("smoothy-light", {
      base: "vs",
      inherit: false,
      rules: [
        { token: "class", foreground: "#1199d3" },
        { token: "method", foreground: "F5A623" },
        { token: "param", foreground: "e83e8c" },
        { token: "comment", foreground: "C2C280" },
        { token: "string", foreground: "d0021b" },
        { token: "number", foreground: "BD10E0" },
        { token: "keywords", foreground: "cc99ff" },
        { token: "identifier", foreground: "660033" },
        { background: "EDF9FA" },
      ],
      colors: {
        "editor.background": "#ffffff",
        "editor.lineHighlightBackground": "#fff2cf",
        "editorLineNumber.foreground": "#999999",
      },
    });

    monaco.editor.defineTheme("smoothy-dark", {
      base: "vs-dark",
      inherit: false,
      rules: [
        { token: "class", foreground: "#7ed6df" },
        { token: "method", foreground: "f6e58d" },
        { token: "param", foreground: "e056fd" },
        { token: "comment", foreground: "95afc0" },
        { token: "string", foreground: "ff7979" },
        { token: "number", foreground: "c56cf0" },
        { token: "keywords", foreground: "74b9ff" },
        { token: "identifier", foreground: "dff9fb" },
        { background: "1e272e" },
      ],
      colors: {
        "editor.background": "#1e272e",
        "editor.lineHighlightBackground": "#2d3436",
        "editorLineNumber.foreground": "#636e72",
      },
    });

    monaco.editor.setTheme(theme === "dark" ? "smoothy-dark" : "smoothy-light");
  }, [monaco, theme]);

  useEffect(() => {
    setSmoothy(initialCode?.smoothy || "");
    onRunCode(initialCode?.smoothy);
  }, [initialCode?.smoothy, reRunCode]);

  const runSteps = (footprints) => {
    let currentStepNumberLocal = 0;

    try {
      const msgArray = footprintsMessages || false;
      const neededSteps = myFootprints;
      const smoothyFootprints = footprints;

      if (!msgArray) {
        console.log("The assertions for this lesson are empty.");
        return;
      }

      const doStepsCheck = (footprints1, footprints2) => {
        const groupByRoots = (footPrints) => {
          const toReturn = [];
          let currentRoot = null;
          let currentChildren = [];

          footPrints.forEach((workOn) => {
            if (currentRoot === null) {
              currentRoot = workOn;
            } else if (currentRoot.id === workOn.id) {
              toReturn.push({
                startRoot: currentRoot,
                endRoot: workOn,
                children: currentChildren,
              });
              currentChildren = [];
              currentRoot = null;
            } else {
              currentChildren.push(workOn);
            }
          });

          return toReturn;
        };

        const grouped1 = groupByRoots(footprints1);
        const grouped2 = groupByRoots(footprints2);
        const map = {};

        for (let i = 0; i < grouped1.length; i++) {
          const step1 = grouped1[i]?.startRoot;
          const step2 = grouped2[i]?.startRoot;
          const mes = msgArray[i];

          if (!step2) {
            throw new Error(mes["step not written"]);
          }

          if (step1.event === step2.event) {
            if (step1.name === step2.name) {
              if (map[step1.id] && map[step1.id] !== step2.id) {
                throw new Error(mes["wrong variable"]);
              }

              map[step1.id] = step2.id;

              for (const key in step1.details) {
                const val1 = step1.details[key];
                const val2 = step2.details[key];

                if (val1 !== val2) {
                  if (key === "location") {
                    if (val1.x !== val2.x || val1.y !== val2.y) {
                      throw new Error(mes["wrong values"]);
                    }
                  } else if (typeof val1 === "string" && typeof val2 === "string") {
                    if (val1.toLowerCase() !== val2.toLowerCase()) {
                      throw new Error(mes["wrong values"]);
                    }
                  } else {
                    throw new Error(mes["wrong values"]);
                  }
                }
              }

              // Increment step counter and update global state
              currentStepNumberLocal++;
              setCurrentStepNumber(currentStepNumberLocal);

              stepSucceeded(step1, currentStepNumberLocal);
            } else {
              throw new Error(mes["wrong method"]);
            }
          } else {
            throw new Error(mes["wrong step"]);
          }
        }
      };

      doStepsCheck(neededSteps, smoothyFootprints);
      addRun(currentStepNumberLocal, true);
      return true;
    } catch (error) {
      stepFailed(error, currentStepNumberLocal);
      addRun(currentStepNumberLocal, false);
      return false;
    }
  };

  const stepSucceeded = () => {
    setCurrentStep(
      lang === "ar"
        ? "🎉تهانينا! يمكنك الانتقال إلى الحلقة التالية. 🎉"
        : "🎉Congrats! You can move on to the next episode. 🎉"
    );
    setIsStepsOver(true);
  };

  const stepFailed = (error, stepNumber) => {
    setIsStepsOver(false);
    setCurrentStep(
      error
        .toString()
        .replace(/^Error:\s*/, "")
        .trim()
    );
    console.error(`❌ Step ${stepNumber} failed:`, error.message);
  };

  const addRun = (stepNumber, success) => {
    console.log(`Run at step ${stepNumber}: ${success ? "Success" : "Failure"}`);
  };

  const createIframeBody = (code, width, height) => `
  <style>
    body {
      overflow:hidden;
      border: 1px solid #d7dbde;
      margin: 0;
      padding: 0;
    }
    #canvas-container {
      width: 100%;
      height: 100%;
    }
  </style>
  <script src="${window.location.origin}/static/jquery-3.5.0.min.js"></script>
  <script src="${window.location.origin}/static/smoothy.js"></script>
  <div id="canvas-container"></div>
  <script>
    const S3_URL = "${process.env.REACT_APP_API_S3_URL}"; // <--- inject the value into iframe's window

    setTimeout(() => {
      $(document).on("smoothy_compiled", (event) => {
        const compileEvent = new CustomEvent("smoothy_compiled", { detail: { code: event.detail.code } });
        parent.document.dispatchEvent(compileEvent);
      });

      var mediaResourceProvider = function(url) {
        return S3_URL + url;
      };

      const smoothyOptions = {
        graphicsDriver: {
          target: '#canvas-container',
          width: ${width + 125},
          height: ${height + 125},
          render(x, y, object) {},
        },
        mediaResourceProvider
      };

      smoothy.default(\`${code}\`, smoothyOptions)
        .then(result => parent.smoothyDone(result, true))
        .catch(error => parent.smoothyDone(error, false));
    }, 0);
  </script>
`;

  const generateIframe = (smoothyParam) => {
    const container = document.getElementById("sandbox-container");

    // Use container width or fallback to 900, then cap it at 1200
    const width = container?.offsetWidth;

    // Maintain a 16:9 aspect ratio (standard widescreen)
    const height = container?.offsetHeight;

    const code = smoothyParam || smoothy;

    $("#sandbox-container").html("");
    const iframe = iframeManager({
      container,
      body: createIframeBody(code, width, height),
      sandboxAttributes: ["allow-same-origin", "allow-scripts"],
      scroll: false,
    });

    $(iframe.iframe)
      .css({ width: `${width}px`, height: `${height}px`, maxWidth: "100%", maxHeight: "100%" })
      .attr("frameborder", "0");
  };

  const onRunCode = (smoothyParam) => {
    setOutput("");
    setLoading(true);
    generateIframe(smoothyParam);
  };

  const showNextHint = () => {
    const currentStepTitle = `Step ${currentStepNumber + 1}`.trim().toLowerCase();
    const currentStep = myHintSteps.find((step) => step.title.trim().toLowerCase() === currentStepTitle);
    if (!currentStep) {
      alert("No hints available for this step.");
      return;
    }
    let nextHintIndex = currentHintIndex + 1;
    if (nextHintIndex >= currentStep.hints.length) {
      nextHintIndex = 0;
    }
    setCurrentHintIndex(nextHintIndex);
    setCurrentStep(currentStep.hints[nextHintIndex].text);
  };

  const resetSnippet = () => {
    setSmoothy(snippet);
    onRunCode(snippet);
  };

  const resetSmoothyCodeAndRunCode = () => {
    resetSmoothyCode();
    onRunCode("");
    setOutput("");
  };

  const handleSmoothyResult = useCallback(
    (result, success) => {
      setLoading(false);
      if (success) {
        setOutput(result.output.replace(/\n/g, "<br />").replace(/"/g, ""));
        if (type == "code") {
          runSteps(result.footprints);
        }

        setMessage("");
      } else {
        setMessage(result.errorMessage);
        setCurrentStep(
          lang === "ar"
            ? "لا بأس بارتكاب الأخطاء، ومن الرائع تصحيحها"
            : "It is OK to make mistakes, and it's great to fix them"
        );

        if (monaco && editorRef.current) {
          const editor = editorRef.current;
          editor.revealPositionInCenter({
            lineNumber: result.error.line,
            column: result.error.column,
          });
          editor.setPosition({
            column: result.error.column,
            lineNumber: result.error.line,
          });
          const decorations = editor.deltaDecorations(
            [],
            [
              {
                range: new monaco.Range(result.error.line, 1, result.error.line, 1),
                options: {
                  isWholeLine: true,
                  className: "error-line",
                  glyphMarginClassName: "error-glyph",
                },
              },
            ]
          );
          setEditorDecorations(decorations);
        }
      }
    },
    [isTeacher, monaco, myFootprints, footprintsMessages, myHintSteps, onRunCode]
  );

  useEffect(() => {
    window.smoothyDone = handleSmoothyResult;

    const resizeHandler = () => generateIframe(smoothy);
    window.addEventListener("resize", resizeHandler);

    return () => {
      window.removeEventListener("resize", resizeHandler);
    };
  }, [handleSmoothyResult, smoothy]);

  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullScreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullScreenChange);
    };
  }, []);

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      outputRef.current?.requestFullscreen().catch((err) => {
        alert(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
      });
    } else {
      document.exitFullscreen();
    }
  };

  return (
    <div className="ltr-ignore relative">
      <SmoothyHeader
        isTeacher={isTeacher}
        isLoading={loading}
        onRunCode={onRunCode}
        isHorizontal={isHorizontal}
        setIsHorizontal={setIsHorizontal}
        showNextHint={showNextHint}
        resetSnippet={resetSnippet}
        snippet={snippet}
        resetSmoothyCodeAndRunCode={resetSmoothyCodeAndRunCode}
        hasCodeContent={hasCodeContent}
      />

      <div className={`flex ${isHorizontal ? "gap-2" : "flex-col space-y-2"}`}>
        <div className="w-[10vh] flex-1 rounded-lg border border-gray-50 bg-white shadow-md dark:border-gray-800 dark:bg-[#1e272e]">
          <Editor
            height={isHorizontal ? "60vh" : "25vh"}
            defaultLanguage="smoothy"
            defaultValue={initialCode?.smoothy || ""}
            value={smoothy}
            onChange={(val) => setSmoothy(val || "")}
            onMount={(editor, monaco) => {
              editorRef.current = editor;
            }}
            options={{
              theme: theme === "dark" ? "smoothy-dark" : "smoothy-light",
              minimap: { enabled: false },
              fontSize: 14,
              wordWrap: "off",
              scrollBeyondLastLine: false,
              smoothScrolling: true,
              renderLineHighlight: "all",
              tabSize: 2,
              formatOnType: true,
              automaticLayout: true,
              cursorStyle: "line",
              folding: true,
              suggestOnTriggerCharacters: true,
              quickSuggestions: true,
              bracketPairColorization: { enabled: true },
              codeLens: true,
              links: true,
              matchBrackets: "always",
              glyphMargin: false,
              contextmenu: true,
            }}
          />
        </div>

        <div v ref={outputRef} className="h-[67vh] flex-1">
          <div className="relative h-full w-full">
            {loading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center rounded bg-white/70 dark:bg-[#1e272e]/70">
                <img
                  src="https://cdn-static-images-frontend.s3-eu-west-1.amazonaws.com/more/spinner.gif"
                  alt="Loading..."
                  className="h-36 w-36"
                />
              </div>
            )}
            {type == "code" && user.role === "student" && <GreenoMessage currentStep={currentStep} />}
            {!output && message && user.role === "student" && type != "code" && (
              <div className="absolute inset-0 z-10 flex items-center justify-center rounded pr-6">
                <div className="flex h-full flex-col items-end gap-6 md:flex-row">
                  <img src={reddoError} alt="Error Illustration" className="h-auto w-24" />
                  <div className="relative">
                    <div
                      className="custom-scrollbar mb-10 max-h-80 overflow-auto whitespace-pre-wrap rounded bg-[#F0E29A] p-4 text-gray-700"
                      dangerouslySetInnerHTML={{ __html: message }}
                    />
                    <div className="clip-triangle absolute bottom-24 left-[-40px] h-10 w-10 bg-[#F0E29A]" />
                  </div>
                </div>
              </div>
            )}

            <div id="sandbox-container" className="sandbox-container relative z-0 h-full w-full"></div>
            <Button
              icon={<BiExpandAlt size={20} />}
              className="absolute bottom-4 right-4 z-20 rounded-lg bg-blue-500 p-2 text-white shadow-md hover:bg-blue-600"
              onClick={toggleFullScreen}
            />
          </div>
        </div>
      </div>

      {!output && message && user.role != "student" && (
        <div
          className="mt-4 max-h-60 overflow-auto whitespace-pre-wrap rounded bg-[#F8D7DA] p-4 text-red-700"
          dangerouslySetInnerHTML={{ __html: message }}
        />
      )}
      {!output && message && user.role == "student" && type == "code" && (
        <div
          className="absolute mt-4 max-h-60 w-full overflow-auto whitespace-pre-wrap rounded bg-[#F8D7DA] p-4 text-red-700"
          dangerouslySetInnerHTML={{ __html: message }}
        />
      )}

      {output && (
        <div
          className="absolute mt-4 max-h-60 w-full overflow-auto rounded bg-green-500 p-4 text-white"
          dangerouslySetInnerHTML={{ __html: output }}
        />
      )}
    </div>
  );
};

export default SmoothyCodeEditor;
